/**
 * src/types/core/primitives.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * All non-chart-specific primitive type aliases, enums, and small constants.
 * Nothing in this file imports from chart modules — it is the dependency root.
 *
 * Chart2Video | CORSphere Software Inc.
 * Schema version: 3
 */

// =============================================================================
// RECORDING & EXPORT
// =============================================================================

/**
 * Export resolution. Controls recording canvas size and video bitrate.
 *   '480p'  →  854×480  @ 2 Mbps
 *   '720p'  → 1280×720  @ 4 Mbps
 *   '1080p' → 1920×1080 @ 8 Mbps   ← default
 *   '1440p' → 2560×1440 @ 16 Mbps
 *   '4k'    → 3840×2160 @ 40 Mbps
 */
export type RecordingQuality = '480p' | '720p' | '1080p' | '1440p' | '4k';

/** The four supported export aspect ratios. */
export type ExportFormat = '16:9' | '9:16' | '1:1' | '4:5';

/**
 * Canvas pixel dimensions for each export format.
 * ALL renderers, spatialUtils, and ChartRecorder read dimensions from here.
 * Never hardcode 1920 or 1080 — always use CANVAS_DIMS[canvasFormat].
 */
export const CANVAS_DIMS: Record<ExportFormat, { width: number; height: number }> = {
  '16:9': { width: 1920, height: 1080 },
  '9:16': { width: 1080, height: 1920 },
  '1:1':  { width: 1080, height: 1080 },
  '4:5':  { width: 1080, height: 1350 },
} as const;

/** Human-readable labels for the toolbar format picker. */
export const EXPORT_FORMAT_LABELS: Record<ExportFormat, string> = {
  '16:9': '16:9  Landscape',
  '9:16': '9:16  Vertical',
  '1:1':  '1:1   Square',
  '4:5':  '4:5   Portrait',
} as const;

/** CSS aspect-ratio string for each format (used in middle panel). */
export const EXPORT_FORMAT_ASPECT: Record<ExportFormat, string> = {
  '16:9': '16 / 9',
  '9:16': '9 / 16',
  '1:1':  '1 / 1',
  '4:5':  '4 / 5',
} as const;


// =============================================================================
// VISUAL PRIMITIVES
// =============================================================================

/** Background fill mode. */
export type BackgroundType = 'image' | 'color' | 'gradient' | 'none';

/** An RGBA colour as a hex string. Must be '#RRGGBB' or '#RGB'. */
export type HexColor = string;

/** Font weight — covers both keyword and numeric values. */
export type FontWeight = 'normal' | 'bold' | '300' | '400' | '500' | '600' | '700' | '900';

/** Font style. */
export type FontStyle = 'normal' | 'italic';

/** Text alignment on canvas. */
export type TextAlign = 'left' | 'center' | 'right';

/**
 * Label density for axis tick labels.
 *   'auto'    → renderer decides based on available space
 *   'all'     → force-show every label regardless of overlap
 *   'sparse'  → show every ~5th label
 *   'compact' → show every ~10th label
 */
export type LabelDensity = 'auto' | 'all' | 'sparse' | 'compact';

/**
 * Horizontal grid line density.
 *   'none'   → no grid lines
 *   'light'  → low-opacity lines (default for most charts)
 *   'normal' → standard opacity
 *   'heavy'  → high-contrast lines
 */
export type GridStyle = 'none' | 'light' | 'normal' | 'heavy';

/**
 * Marker shape for data points.
 * Renderer-supported values (all handled in ChartUtils.drawSinglePoint):
 *   circle / square / triangle / diamond / star / cross  — vector shapes
 *   image       — custom image rendered as circular crop
 *   imageShape  — custom image clipped to the selected shape
 *   text        — text glyph rendered at the point
 *   icon        — named icon glyph
 */
export type PointType =
  | 'circle' | 'square' | 'triangle' | 'diamond' | 'star' | 'cross'
  | 'image' | 'imageShape' | 'text' | 'icon';

/**
 * Named region of the canvas.
 * Derived from coordinates by getCanvasZone() in spatialUtils.ts.
 * NEVER set by an agent directly — always computed from coordinates.
 */
export type CanvasZone =
  | 'top-left'    | 'top-centre'    | 'top-right'
  | 'mid-left'    | 'centre'        | 'mid-right'
  | 'bottom-left' | 'bottom-centre' | 'bottom-right';

/** Semantic role of a canvas element. */
export type ElementClass = 'semantic' | 'visual' | 'chart-body';

/**
 * Overlap policy enforced by validateChartConfig.
 *   'forbidden'          — bounding box must not intersect any other element
 *   'semantic-forbidden' — no overlap with semantic; visual overlap is OK
 *   'allowed'            — no restrictions (decorative elements only)
 */
export type OverlapPolicy = 'forbidden' | 'semantic-forbidden' | 'allowed';

/** Semantic role of a positionable canvas element. */
export type PositionElementType = 'title' | 'subtitle' | 'infoBox' | 'chart';

/** Annotation element type. */
export type AnnotationType = 'text' | 'arrow' | 'box' | 'markerLine' | 'highlight';

/**
 * How heat map cell values are displayed.
 *   'auto'       → renderer picks based on value range
 *   'percentage' → appends % symbol
 *   'raw'        → plain number, no formatting
 *   'integer'    → rounds to nearest whole number
 *   'decimal'    → one decimal place
 */
export type HeatMapValueFormat = 'auto' | 'percentage' | 'raw' | 'integer' | 'decimal';

/**
 * Format for data labels drawn on chart elements.
 * Valid combinations per chart type — enforced by validateChartConfig:
 *   bar / line   → 'value' | 'name' | 'name+value'
 *   pie          → 'value' | 'percentage' | 'name' | 'name+value' | 'name+percent'
 *   heatmap      → 'value' | 'percentage'
 */
export type DataLabelFormat = 'value' | 'percentage' | 'name' | 'name+value' | 'name+percent';

/**
 * Where the legend block is rendered.
 *   'inline' → labels drawn adjacent to data elements (default)
 *   others   → legend block drawn in that canvas region
 */
export type LegendPosition = 'top' | 'bottom' | 'left' | 'right' | 'inline' | 'end';

/** Line chart legend format. */
export type LineLegendFormat = 'name+value' | 'name' | 'value' | 'xy';

/** Line dash pattern for marker lines and arrow shafts. */
export type LineDash = 'solid' | 'dashed' | 'dotted';

/** Arrow head style. */
export type ArrowHeadStyle = 'filled' | 'open' | 'none';

/**
 * How an annotation is positioned on the canvas.
 *   'free'       — positioned by annotation.position (canvas ctx coords)
 *   'dataPoint'  — relative to a specific data point
 *   'yValue'     — at a specific y-axis value
 *   'xCategory'  — at a specific x-axis category name
 *   'pieSlice'   — at the midpoint of a specific pie slice
 */
export type AnnotationBinding = 'free' | 'dataPoint' | 'yValue' | 'xCategory' | 'pieSlice';

/**
 * When the annotation becomes visible during animation.
 *   'withBinding'  — appears when its bound element reaches ~70% visibility
 *   'atProgress'   — appears at a fixed animation progress value
 *   'immediately'  — visible from progress 0
 */
export type AnnotationTiming = 'withBinding' | 'atProgress' | 'immediately';

/** Every clickable canvas element maps to one of these types. */
export type SelectedElementType =
  | 'title' | 'subtitle' | 'infoBox'
  | 'bar' | 'slice' | 'point' | 'cell'
  | 'xAxis' | 'yAxis' | 'chartBody'
  | 'annotation';


// =============================================================================
// TYPOGRAPHY
// =============================================================================

/**
 * Font families available for user selection.
 * Must be reliably available in Canvas 2D context across all major browsers
 * without external loading.
 */
export const VALID_FONT_FAMILIES = [
  'Arial', 'Helvetica', 'Verdana', 'Georgia',
  'Times New Roman', 'Courier New', 'Impact', 'Trebuchet MS',
] as const;

export type FontFamily = typeof VALID_FONT_FAMILIES[number];


// =============================================================================
// BRAND COLORS — mirror of CSS custom properties in index.css
// =============================================================================
// CSS variables can't be read from an offscreen canvas.
// Chart renderers use these constants directly when drawing defaults.
// Keep in sync with :root { --c2v-* } in src/index.css.

export const BRAND_COLORS = {
  primary:         '#f26419',
  primaryDark:     '#d44f06',
  primaryLight:    '#fff4ec',
  primaryMid:      '#ffd5b8',
  accent:          '#0fb8a0',
  accentDark:      '#0a9688',
  accentLight:     '#e6f9f6',
  rose:            '#f04e6b',
  gold:            '#f5a623',
  dark:            '#12152e',
  darkMid:         '#1e2140',
  textDark:        '#1a1710',
  textMid:         '#4a4540',
  textMuted:       '#9a9490',
  textOnDark:      '#f0ece8',
  bg:              '#f7f5f2',
  surface:         '#ffffff',
  surfaceWarm:     '#fdf8f4',
  border:          '#ece8e2',
  borderStrong:    '#d6cfc6',
} as const;

export type BrandColor = keyof typeof BRAND_COLORS;


// =============================================================================
// BREAKPOINTS — mirror of @media queries in index.css
// =============================================================================

export const BREAKPOINTS = {
  phone:   479,
  mobile:  767,
  tablet:  899,
  laptop:  1099,
  desktop: 1279,
} as const;

export type Breakpoint = keyof typeof BREAKPOINTS;
